/**
 * Incidents IT › Corrections (décision du chef de projet du 28/10/2026, D-IT-036, US #141) : règles pures partagées par
 * l'API, le front et le mode maquette.
 *
 * Le titre, la description ou un commentaire d'un incident se corrigent jusqu'au statut « résolu », non compris. Chaque
 * correction AJOUTE au fil un commentaire « trace » (jamais un commentaire modifié ni supprimé) :
 *  - titre, description : la trace garde le texte d'origine (le nouveau est sur l'incident) ;
 *  - commentaire : la trace porte le nouveau texte et renvoie au message corrigé, resté intact, dont elle prend la
 *    visibilité (la correction d'une note interne reste une note interne, jamais lue par le demandeur). Le fil affiche
 *    la dernière version ; les précédentes se consultent dans l'historique du message (D-IT-129).
 * Message d'un autre (réponses de Benoit à #265 et de Vincent à #408) : seulement pour retirer une donnée personnelle
 * collée par erreur, par un administrateur du support (`modifier` ET `administrer` sur la filiale de l'incident), avec
 * un motif obligatoire ; les versions antérieures à cette correction ne se lisent plus qu'avec ces mêmes droits, les
 * autres lecteurs voient la version corrigée et la mention de la correction (qui, quand, motif). L'effacement réel
 * passe par le script ponctuel du DSI (AGENTS.md). Les corrections faites par l'auteur ne changent pas (D-IT-129).
 * Le texte n'est jamais copié dans l'audit ni dans une notification.
 */
import { auteurCommentaire, refusCommentaire } from './commentaires';
import type { RefusActeurIncident, StatutIncident } from './incidents';

/** Ce qu'on corrige ; aussi la nature d'une trace (colonne it.commentaire.correction). */
export const CIBLES_CORRECTION = ['titre', 'description', 'commentaire'] as const;
export type CibleCorrection = (typeof CIBLES_CORRECTION)[number];

/** Statuts où l'incident se corrige : jusqu'à « résolu », non compris (un incident rouvert redevient corrigeable). */
export const STATUTS_CORRECTION: readonly StatutIncident[] = ['nouveau', 'qualifie', 'affecte', 'en-traitement', 'en-attente'];

/** L'incident se corrige encore : statut avant « résolu » et non archivé ; sinon 409 etat_incompatible. */
export function correctionPossible(incident: { statut: StatutIncident; archiveLe: string | null }): boolean {
  return !incident.archiveLe && STATUTS_CORRECTION.includes(incident.statut);
}

/** Refus d'une correction (403) : ceux des commentaires, plus `non-auteur` (message d'un autre). */
export type RefusCorrection = RefusActeurIncident | 'non-auteur';

const memePersonne = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * Administrateur du support sur l'incident (#408, réponse de Vincent du 03/11/2026) : agent (`modifier`) qui a aussi
 * `administrer` sur la filiale de l'incident – en production AGORA-GRP-IT-ADMIN. `administrer` seul ne suffit pas (le
 * profil de pilotage ne corrige pas le contenu des autres) ; le demandeur, même administrateur, reste participant sur son
 * propre incident (auteurCommentaire, comme pour les notes internes). Seul à corriger le message d'un autre et à lire
 * les versions qui précèdent une telle correction.
 */
export function administrateurSupport(
  incident: { demandeur: string; declarant: string },
  moi: string,
  estAgent: boolean,
  administrer: boolean,
): boolean {
  return administrer && auteurCommentaire(incident, moi, estAgent) === 'agent';
}

/** Correction du message d'un autre que soi (#408) : motif obligatoire (schemaCorrectionMessageAutre). */
export const correctionParUnAutre = (message: { auteur: string }, moi: string) => !memePersonne(message.auteur, moi);

/**
 * Refus d'acteur d'une correction (403), ou `null` :
 *  - titre, description : le demandeur (même agent : il corrige alors en tant que demandeur, D-IT-124 ne vise que le
 *    traitement) et tout agent qui voit l'incident, pas seulement l'agent affecté (D-IT-126, décidé) ; `non-demandeur`
 *    sinon (déclarant non agent compris) ;
 *  - commentaire : son auteur, s'il peut encore écrire cette visibilité (refusCommentaire : un ancien agent ne corrige
 *    plus sa note interne) ; le message d'un autre, l'administrateur du support seul (`administrer` : droit sur la
 *    filiale de l'incident, #408), pour retirer une donnée personnelle, avec un motif ; `non-auteur` sinon.
 */
export function refusCorrection(
  cible: CibleCorrection,
  incident: { demandeur: string; declarant: string },
  moi: string,
  estAgent: boolean,
  message?: { auteur: string; interne: boolean },
  administrer = false,
): RefusCorrection | null {
  if (cible !== 'commentaire') {
    return incident.demandeur === moi || auteurCommentaire(incident, moi, estAgent) === 'agent' ? null : 'non-demandeur';
  }
  if (!message) return 'non-auteur';
  if (correctionParUnAutre(message, moi) && !administrateurSupport(incident, moi, estAgent, administrer)) return 'non-auteur';
  return refusCommentaire(message, incident, moi, estAgent);
}

/**
 * Versions réservées d'un message (#408) : celles qui précèdent sa dernière correction par un autre que son auteur (trace
 * avec un motif) ; aucune sinon. `versions` : le message d'origine et ses corrections, du plus ancien au plus récent
 * (date, puis identifiant, comme l'API). Même règle que le SQL de l'API (commentaires.ts), pour la maquette.
 */
export function versionsReservees(versions: readonly { id: string; motif: string | null }[]): Set<string> {
  const derniere = versions.findLastIndex((v) => v.motif !== null);
  return new Set(versions.slice(0, Math.max(derniere, 0)).map((v) => v.id));
}

/** Lecture du texte des versions réservées (#408) : l'administrateur du support seul, avec les droits de la correction. */
export const lectureVersionsReservees = administrateurSupport;

/** Message d'origine (pas une trace) : seul un tel message se corrige ; ses corrections lui renvoient. */
export const estMessageOrigine = (c: { correction: CibleCorrection | null }) => c.correction === null;

/**
 * Auteur d'origine à prévenir d'une correction (#267, réponse de Benoit du 02/11/2026), ou `null` : il est prévenu dès
 * qu'un autre corrige son contenu, sans détection de faute de frappe (trop fragile). L'auteur du titre et de la
 * description est le demandeur (D-IT-036) ; celui d'un message, son auteur. Qui corrige son propre contenu ne prévient
 * personne, pas même l'agent affecté. La notification ne porte jamais le texte.
 */
export function auteurAPrevenir(
  cible: CibleCorrection,
  incident: { demandeur: string },
  moi: string,
  message?: { auteur: string },
): string | null {
  const auteur = cible === 'commentaire' ? message?.auteur : incident.demandeur;
  return auteur && auteur.toLowerCase() !== moi.toLowerCase() ? auteur : null;
}
